import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AuthPageShell from "../components/AuthPageShell";
import TotpForm from "../components/TotpForm";
import { AuthApi } from "../../infrastructure/api/AuthApi";
import { SessionManager } from "../../infrastructure/session/SessionManager";
import {
  AUTH_FLOW_PURPOSE_KEY,
  AUTH_FLOW_PURPOSE_RESET_TOTP,
} from "../../domain/constants/authFlowConstants";
import { isTotpAlreadyEnabledMessage } from "../../domain/utils/readTotpEnabled";
import { authTextButtonStyle } from "../theme/authTextButtonStyle";

const ScanQRPage = () => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [qrUri, setQrUri] = useState("");
  const [manualKey, setManualKey] = useState("");
  const [changeText, setChangeText] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // GET /auth/totp/setup issues a new secret per call; a second request (e.g.
  // StrictMode's double effect) would leave the shown QR out of sync with the
  // secret the server verifies against.
  const qrRequestedRef = useRef(false);

  const isReconfigure =
    sessionStorage.getItem(AUTH_FLOW_PURPOSE_KEY) ===
    AUTH_FLOW_PURPOSE_RESET_TOTP;

  useEffect(() => {
    if (qrRequestedRef.current) return;
    qrRequestedRef.current = true;

    const fetchTotpQr = async () => {
      setLoading(true);
      setError(null);

      const session = new SessionManager();

      if (!isReconfigure && session.getIsTotpEnabled()) {
        setLoading(false);
        setError(t("scanQr.alreadyConfigured"));
        return;
      }

      try {
        const api = new AuthApi();

        const token = isReconfigure
          ? session.getAccessToken()
          : session.getTempAccessToken() || session.getAccessToken();

        if (!token) {
          throw new Error(
            isReconfigure
              ? t("scanQr.sessionExpiredReconfigure")
              : t("scanQr.sessionExpiredSetup"),
          );
        }

        const response = await api.getQrCode(token);
        setQrUri(response.qr_code_uri);
        setManualKey(response.secret);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : t("scanQr.genericQrError");

        if (!isReconfigure && isTotpAlreadyEnabledMessage(message)) {
          sessionStorage.setItem("is_totp_enabled", "true");
          setError(t("scanQr.alreadyConfigured"));
        } else {
          setError(message);
        }

        console.error("Failed to fetch TOTP QR:", err);
      } finally {
        setLoading(false);
      }
    };

    void fetchTotpQr();
  }, [isReconfigure]);

  const handleCopy = async () => {
    if (!manualKey) return;
    await navigator.clipboard.writeText(manualKey);
    setChangeText(true);
    setTimeout(() => setChangeText(false), 2000);
  };

  const showAlreadyConfiguredActions = Boolean(error) && !qrUri && !isReconfigure;

  return (
    <AuthPageShell
      title={t("scanQr.title")}
      subtitle={
        isReconfigure
          ? t("scanQr.subtitleReconfigure")
          : t("scanQr.subtitleSetup")
      }
    >
      <div className="mb-6 flex min-h-[172px] items-center justify-center">
        {loading ? (
          <p className="text-sm text-gray-500">{t("scanQr.generating")}</p>
        ) : qrUri ? (
          <div className="rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
            <QRCodeSVG value={qrUri} size={150} level="H" />
          </div>
        ) : (
          <p className="px-2 text-center text-sm text-red-500">
            {error ?? t("scanQr.genericQrError")}
          </p>
        )}
      </div>

      {showAlreadyConfiguredActions && (
        <div className="mb-6 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => navigate("/setup-twofa")}
            className="text-primary"
            style={authTextButtonStyle}
          >
            {t("scanQr.continueSignInSetup")}
          </button>
          <button
            type="button"
            onClick={() => navigate("/settings")}
            className="text-primary"
            style={authTextButtonStyle}
          >
            {t("scanQr.goToSettingsReconfigure")}
          </button>
        </div>
      )}

      {qrUri && (
        <>
          <div className="mb-6 flex flex-col gap-3 rounded-xl border border-blue-100 bg-blue-50 p-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="break-all text-xs text-gray-700">
              <span className="font-semibold">{t("scanQr.manualKey")}</span>{" "}
              {loading ? t("scanQr.generating") : manualKey || "-"}
            </p>

            <button
              type="button"
              onClick={() => void handleCopy()}
              disabled={!manualKey || loading}
              className="flex shrink-0 items-center justify-center gap-1 text-primary transition disabled:cursor-not-allowed disabled:opacity-50"
              style={authTextButtonStyle}
            >
              <ContentCopyIcon fontSize="small" />
              {changeText ? t("scanQr.copied") : t("scanQr.copy")}
            </button>
          </div>

          <TotpForm verifyAction="twofaEnabled" />
        </>
      )}
    </AuthPageShell>
  );
};

export default ScanQRPage;
